import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridItem from "@insite/mobius/GridItem";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import {
    getItemTrackingCollection,
    ItemTrackingCollectionParam,
    itemTrackingExportUrl,
} from "@tKgpco/Services/ItemTrackingService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadItemTrackingCollection from "@tKgpco/Store/Data/ItemTracking/Handlers/LoadItemTrackingCollection";
import resetItemTrackingCollection from "@tKgpco/Store/Data/ItemTracking/Handlers/ResetItemTrackingCollection";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const itemTrackingParams = parseQueryString<ItemTrackingCollectionParam>(location.search);
    return {
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isLoggedIN: state.context.session.isAuthenticated,
        ItemTrackingStore: state.KGPState.ItemTrackingStore,
        location,
        itemTrackingParams,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadItemTrackingCollection,
    resetItemTrackingCollection,
};

export interface ItemTrackingContainerStyles {
    backToReports?: ButtonPresentationProps;
}

export const itemTrackingContainerStyles: ItemTrackingContainerStyles = {
    backToReports: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            float: right;
            text-transform: uppercase;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            min-width: 120px;
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            & :hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
            }
        `,
    },
};

const styles = itemTrackingContainerStyles;

const ItemTrackingContainer: React.FC<Props> = ({
    id,
    userName,
    signInUrl,
    currentUserIsGuest,
    history,
    ItemTrackingStore,
    itemTrackingParams,
    loadItemTrackingCollection,
    resetItemTrackingCollection,
}) => {
    useEffect(() => {
        resetItemTrackingCollection();
    }, []);

    useEffect(() => {
        if (!(userName && !currentUserIsGuest)) {
            history.push(`${signInUrl}`);
        }
    }, [userName, currentUserIsGuest]);

    useEffect(() => {
        if (
            !ItemTrackingStore.isLoading &&
            !ItemTrackingStore.itemTrackingCollection &&
            userName &&
            !currentUserIsGuest
        ) {
            loadItemTrackingCollection({ ...itemTrackingParams });
        }
    }, [ItemTrackingStore, userName, currentUserIsGuest]);

    const exportExcel = () => {
        if (typeof window === "undefined") return;

        window
            .fetch(itemTrackingExportUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ ...itemTrackingParams, isExport: true, page: null }),
            })
            .then(response => {
                if (!response.ok) throw new Error("Export failed");
                return response.blob();
            })
            .then(blob => {
                const now = new Date();

                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, "0");
                const day = String(now.getDate()).padStart(2, "0");

                const hours24 = now.getHours();
                const hours12 = String(hours24 % 12 || 12).padStart(2, "0");

                const minutes = String(now.getMinutes()).padStart(2, "0");
                const seconds = String(now.getSeconds()).padStart(2, "0");

                const ampm = hours24 >= 12 ? "PM" : "AM";

                const timestamp = `${year}${month}${day}_${hours12}${minutes}${seconds}_${ampm}`;
                const businessCase =
                    itemTrackingParams.businessCaseId === "1"
                        ? "Verizon"
                        : itemTrackingParams.businessCaseId === "7"
                          ? "Vartac"
                          : "";

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = `${businessCase}_Item_Tracking_File_${timestamp}.xlsx`;

                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
            })
            .catch(err => {
                // console.error(err);
                // alert("Export failed");
            });
    };

    return (
        <StyledWrapper>
            <GridItem
                width={12}
                css={css`
                    justify-content: space-between;
                    align-items: center;
                    margin: 20px 0;
                `}
            >
                <GridItem width={6}>
                    <Zone contentId={id} zoneName="resultCount" />
                </GridItem>
                <GridItem
                    width={6}
                    style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "15px" }}
                >
                    <Button
                        onClick={() => {
                            history.push("MyAccount/Reports");
                        }}
                        {...styles.backToReports}
                    >
                        {translate("Back to Reports")}
                    </Button>
                    {ItemTrackingStore.itemTrackingCollection?.itemTrackings.length ? (
                        <Button onClick={exportExcel}>{translate("Export to Excel")}</Button>
                    ) : null}
                </GridItem>
            </GridItem>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ItemTrackingContainer)),
    definition: {
        group: "Common",
        displayName: "Item Tracking Container",
        fieldDefinitions: [],
    },
};

export default widgetModule;
